import { updateComplianceItem } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeaturedIcon,
  FieldWrapper,
  Icons,
  Label,
  LoadingIndicator,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { ComplianceStatus } from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';

interface ArchiveFilingModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  filingTitle: string;
  entityName?: string;
  isCustom?: boolean;
  onSuccess: () => void;
}

const ArchiveFilingModal = ({
  isOpen,
  onClose,
  filingId,
  filingTitle,
  entityName,
  isCustom,
  onSuccess,
}: ArchiveFilingModalProps) => {
  const { setNotification } = useNotification();

  const [reason, setReason] = useState('');

  const isSubmitting = useToggle(false);

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const onArchive = async () => {
    isSubmitting.on();

    const res = await updateComplianceItem(filingId, {
      status: ComplianceStatus.ARCHIVED,
      reasonToArchive: reason,
    });

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to archive filing.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Filing archived successfully.',
    });

    mixpanel.track('CALENDAR_FILING_ARCHIVED', {
      source: MIXPANEL_SOURCE.CALENDAR,
      surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
      is_custom: isCustom === true,
    });

    await onSuccess();
    isSubmitting.off();
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-lg items-start border-b-0 pb-0">
          <FeaturedIcon
            size="lg"
            variant="warning"
            icon={<Icons.AlertTriangle />}
          />
          <div className="space-y-xs">
            <DialogTitle>Archive filing?</DialogTitle>
            <DialogDescription>
              Tell us more about why you wish to archive{' '}
              <span className="font-bold">{filingTitle}</span>
              {entityName ? <> for {entityName}</> : null}. Once archived,
              notifications and updates for the filing will be stopped.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label>
              Tell us why
              <span className="text-utility-violet-600 ml-xxs">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Type here..."
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onArchive}
            size="md"
            disabled={!reason || isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveFilingModal;
